// ─────────────────────────────────────────────────────────────────────────────
// charge_engine.ts — Spell composition timer
//
// When a spell is cast, the gesture's stroke LENGTH determines the charge time.
// A longer stroke = more time = more room to compose sub-spells.
//
// The "wavefront" advances from 0→1 over chargeDurationMs.
// SpellBranches fire when the wavefront crosses their anchorT.
// The root spell fires at wavefront = 1.0.
//
// Visually: the original ink trace "charges" from the initiator point toward
// the release point.  Branches spark off mid-trail at their anchors.
// ─────────────────────────────────────────────────────────────────────────────

import type { SpellBranch } from './spell_registry.js';

export type BranchFiredCallback = (branch: SpellBranch, wavefrontT: number) => void;
export type SpellResolvedCallback = () => void;
export type ChargeTickCallback = (wavefrontT: number) => void;

export interface ChargeSession {
  spellId: string;
  strokeLengthPx: number;
  chargeDurationMs: number;
  branches: SpellBranch[];
  startedAt: number;
}

export class ChargeEngine {
  private _session: ChargeSession | null = null;
  private _animHandle: number | null = null;
  private _firedBranches = new Set<string>();

  private _onBranch: BranchFiredCallback | null = null;
  private _onResolved: SpellResolvedCallback | null = null;
  private _onTick: ChargeTickCallback | null = null;

  onBranchFired(cb: BranchFiredCallback): void { this._onBranch = cb; }
  onResolved(cb: SpellResolvedCallback): void { this._onResolved = cb; }
  onTick(cb: ChargeTickCallback): void { this._onTick = cb; }

  start(session: ChargeSession): void {
    this.cancel();
    // Enforce a minimum charge so every spell goes through the trace animation
    const clampedDuration = Math.max(session.chargeDurationMs, 180);
    this._session = { ...session, chargeDurationMs: clampedDuration, startedAt: performance.now() };
    this._firedBranches.clear();

    this._tick();
  }

  cancel(): void {
    if (this._animHandle !== null) {
      cancelAnimationFrame(this._animHandle);
      this._animHandle = null;
    }
    this._session = null;
    this._firedBranches.clear();
  }

  get isCharging(): boolean {
    return this._session !== null;
  }

  private _tick = (): void => {
    const s = this._session;
    if (!s) { return; }

    const elapsed = performance.now() - s.startedAt;
    const t = Math.min(elapsed / s.chargeDurationMs, 1.0);

    this._onTick?.(t);

    // Fire any branches whose anchor has been passed
    for (const branch of s.branches) {
      const key = `${branch.anchorT}_${branch.subSpellId}`;
      if (!this._firedBranches.has(key) && t >= branch.anchorT) {
        this._firedBranches.add(key);
        this._onBranch?.(branch, t);
      }
    }

    if (t >= 1.0) {
      this._session = null;
      this._onResolved?.();
      return;
    }

    this._animHandle = requestAnimationFrame(this._tick);
  };
}

// ── Stroke length utility ────────────────────────────────────────────────────

import type { StrokePoint } from '../shared/notebook_model.js';

export function computeStrokeLength(points: StrokePoint[]): number {
  let len = 0;
  for (let i = 1; i < points.length; i++) {
    const dx = points[i].x - points[i - 1].x;
    const dy = points[i].y - points[i - 1].y;
    len += Math.sqrt(dx * dx + dy * dy);
  }
  return len;
}
